"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import RetirementStepsTitle from "./RetirementStepsTitle";
import RetirementPlanCard from "../ui/RetirementPlanCard";
import { formatIndianNumber } from "@/utils/commonFunctions";
import { fmtINR } from "../ui/RetirementBucketGrid";
import { Plan, RetirementPlanCardData } from "@/type/financialPageTypes";
import {
  buildPlanCard,
  computeRetirementBuckets,
  DEFAULT_RETIREMENT_DURATION,
  getBucketAllocation,
  MAX_CUSTOM_RETIREMENT_CORPUS,
  MIN_CUSTOM_RETIREMENT_CORPUS,
} from "@/utils/financialPageFunctions";

export const POST_RETIREMENT_PLAN_CORPUS: Record<NonNullable<Plan>, number> = {
  "SILVER PLAN": 1_00_00_000,
  "GOLD PLAN": 3_00_00_000,
  "PLATINUM PLAN": 10_00_00_000,
};

// Preview rows are computed off a fixed reference duration since the user
// hasn't chosen their retirement duration yet at this step.
function buildPostRetirementPlanCard(id: NonNullable<Plan>, withdrawal?: string): RetirementPlanCardData {
  const corpus = POST_RETIREMENT_PLAN_CORPUS[id];
  const buckets = computeRetirementBuckets(corpus, DEFAULT_RETIREMENT_DURATION, getBucketAllocation(corpus));

  const calcWithdrawal = fmtINR(
    buckets.pensionMonthly * DEFAULT_RETIREMENT_DURATION * 12 +
      buckets.healthGrows +
      buckets.vacationAnnual * DEFAULT_RETIREMENT_DURATION +
      buckets.legacyGrows,
  );

  return buildPlanCard(id, id, `Invest ${fmtINR(corpus)}`, withdrawal ?? calcWithdrawal, [
    { label: "Pension", value: `${fmtINR(buckets.pensionMonthly)}/M` },
    { label: "Health Reserve", value: fmtINR(buckets.healthGrows) },
    { label: "Vacations", value: `${fmtINR(buckets.vacationAnnual)}/Yr` },
    { label: "Legacy", value: fmtINR(buckets.legacyGrows) },
  ]);
}

const PLANS: RetirementPlanCardData[] = [
  buildPostRetirementPlanCard("SILVER PLAN"),
  buildPostRetirementPlanCard("GOLD PLAN"),
  buildPostRetirementPlanCard("PLATINUM PLAN"),
];

export default function PostRetirementCorpusScreen({
  stepTitle,
  selectedPlan,
  onSelectPlanAction,
  customAmount,
  onCustomAmountAction,
  validateTrigger,
}: {
  stepTitle?: string;
  selectedPlan: Plan;
  onSelectPlanAction: (p: Plan) => void;
  customAmount: string;
  onCustomAmountAction: (v: string) => void;
  validateTrigger?: number;
}) {
  const [error, setError] = useState("");
  const customAmountParsed = useMemo(() => parseFloat(customAmount.replace(/,/g, "")) || 0, [customAmount]);

  const validate = () => {
    if (selectedPlan) {
      setError("");
      return true;
    }
    if (!customAmount || customAmountParsed < MIN_CUSTOM_RETIREMENT_CORPUS) {
      setError(`Choose a plan or enter corpus(Min.${fmtINR(MIN_CUSTOM_RETIREMENT_CORPUS)})`);
      return false;
    }
    if (customAmountParsed > MAX_CUSTOM_RETIREMENT_CORPUS) {
      setError(`Retirement corpus cannot exceed ${fmtINR(MAX_CUSTOM_RETIREMENT_CORPUS)}.`);
      return false;
    }
    setError("");
    return true;
  };

  const prevTrigger = useRef(0);
  useEffect(() => {
    if (!validateTrigger || validateTrigger === prevTrigger.current) return;
    prevTrigger.current = validateTrigger;
    validate();
  }, [validateTrigger]);

  const handleSelectPlan = (id: NonNullable<Plan>) => {
    onSelectPlanAction(selectedPlan === id ? null : id);
    onCustomAmountAction("");
    setError("");
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^0-9]/g, "");
    onSelectPlanAction(null);
    if (digits === "") {
      onCustomAmountAction("");
      return;
    }
    const parsed = parseInt(digits, 10);
    if (isNaN(parsed)) return;
    setError("");
    onCustomAmountAction(Math.min(parsed, MAX_CUSTOM_RETIREMENT_CORPUS).toString());
  };

  return (
    <div className="flex flex-col h-full">
      <RetirementStepsTitle
        stepTitle={stepTitle || "What is your Retirement Corpus?"}
        stepSubTitle="Select your retirement corpus and withdraw your monthly income, health cover, and legacy wealth."
      />

      <div className="flex gap-3 pb-4 overflow-x-auto overflow-y-hidden sm:overflow-visible scrollbar-none">
        {PLANS.map((plan) => (
          <RetirementPlanCard
            key={plan.id}
            plan={plan}
            selectedPlan={selectedPlan}
            onSelect={handleSelectPlan}
            isPostRetirement
          />
        ))}
      </div>

      <div className="mt-auto pb-3 relative flex flex-col xs:flex-row items-center justify-between gap-1 xs:gap-4">
        <p className="sm:text-xl font-semibold text-primaryBlack xs:whitespace-nowrap">Custom Amount?</p>
        <label className="w-full xs:max-w-80 sm:max-w-124.25 flex items-center gap-2 sm:gap-3 border border-[#DBDBDB] rounded-2lg sm:rounded-2xl text-lg sm:text-2xl font-semibold sm:font-bold p-2.5 sm:px-4 sm:py-5 bg-white">
          <span className="text-primaryBlack">₹</span>
          <input
            type="text"
            inputMode="numeric"
            placeholder="75,000"
            value={customAmountParsed > 0 ? formatIndianNumber(customAmountParsed) : ""}
            onChange={handleCustomAmountChange}
            onBlur={validate}
            className="flex-1 bg-transparent text-primaryBlack placeholder-gray-400 outline-hidden"
          />
        </label>

        {error && (
          <p className="absolute -bottom-1.5 sm:-bottom-2 inset-x-0 text-xs text-red-500 text-center">{error}</p>
        )}
      </div>
    </div>
  );
}
